// bootstrap.ts — the dice module's composition root / TRUST LAYER (sc-54712, sc-55115).
//
// This is the ONE place allowed to touch the launch ticket and to decide REAL-vs-FUN. Game code (main UI,
// engine/, ui/, pf/) must NEVER import ./launch or ./dev/fake-platform-client — it only ever sees the opaque
// GameSdk produced here. The DPoP-bound REAL client + the /v1 money calls live INSIDE the shared package
// (createGameSDK({ rgsApiMode:"real" })), which never exports them, so a game bundle mounted on this SDK cannot
// reach the non-extractable key or ride the player's money session. Enforced by scripts/check-sdk-boundary.mjs.

import { createGameSDK, type GameSdk, type KeyValueStore } from "@blocklabsdev/rgs-game-manager-frontend-api";
import { apiOrigin } from "./api-origin";
import { takeLaunch, markRealSession, rebootOnLaunchChange, buildLaunchUrl } from "./launch";
import { FakePlatformClient } from "./dev/fake-platform-client";
import { diceResolver } from "./dice-resolver";
import { telemetryContext } from "./telemetry";
import { loaderConfig, readLoaderMode } from "./loader";

// The casino's signals (SC-61815, SC-62154): game.pause / game.resume / ui.blocked / balance.refresh reach the game
// through the SDK's casino bridge, which listens to the WINDOW THAT EMBEDS the game and to nobody else — no list of
// casino origins, in the build or anywhere (the RGS does not enumerate an operator's domains; a REAL session is
// defended by the launch ticket, DPoP and the partitioned cookie, not by who frames the page). A game that is not
// framed has no bridge: the SDK listens to nobody and posts nothing. These signals are presentation holds, never
// authorization: whoever embeds the game can also resume it (operator-signals.ts).
const CASINO_BRIDGE = { casinoBridge: "parent" } as const;

export interface DiceLaunch {
  /** How this document was launched (launch-decision.ts): REAL fresh / REAL resume / the FUN demo / not a launch. */
  readonly kind: "real-fresh" | "real-resume" | "fun" | "launch-required";
  /** The single-use launch ticket taken from the URL fragment (only for a fresh REAL launch). */
  readonly launchTicket: string | undefined;
  /** True when this is (or resumes) a REAL-money session. */
  readonly realLaunch: boolean;
}

// takeDiceLaunch decides the money mode from the LAUNCH and takes the single-use ticket SYNCHRONOUSLY. Call it FIRST,
// before any await: takeLaunch() takes the ticket out of location.href immediately so it can't linger for a Referer /
// analytics / error reporter to capture. The mode is never read from storage (SC-61814): a REAL reload resumes REAL
// because the document's own URL says #mode=real, not because some earlier session left a marker on the origin. It
// also arms the reboot on a launch-fragment change / back-forward-cache restore, so the mode is always a fresh boot's;
// `freeze` (boot.ts) stops the running game before that reload, so the outgoing mode cannot start another round.
export function takeDiceLaunch(freeze: () => void): DiceLaunch {
  const decision = takeLaunch();
  rebootOnLaunchChange(freeze);
  return {
    kind: decision.kind,
    launchTicket: decision.kind === "real-fresh" ? decision.ticket : undefined,
    realLaunch: decision.kind === "real-fresh" || decision.kind === "real-resume",
  };
}

// FUN keeps the SDK's own state in memory (SC-61814): the SDK persists the session per game under one key, so a FUN boot
// on the shared store would overwrite the REAL session record a REAL tab of this origin resumes from. Every FUN boot is
// a fresh launch that never restores or replays, so nothing is lost. (The fake's demo wallet persists on its own key.)
function memoryStore(): KeyValueStore {
  const values = new Map<string, string>();
  return {
    getItem: async (key) => values.get(key) ?? null,
    setItem: async (key, value) => {
      values.set(key, value);
    },
    removeItem: async (key) => {
      values.delete(key);
    },
  };
}

// The demo wallet. Its bet bounds are the RGS's own for Dice — any positive amount the balance covers (the runtime has
// no minimum or maximum bet; the game clamps the stake to its max-profit cap and to the wallet): the fake's defaults
// (0.10 to 10,000.00) would refuse stakes the real game accepts, with a code that means "outcome unknown" (SC-61817).
export const FUN_WALLET = { currency: "USD", balanceMinor: 1_000_000, minBetMinor: 1, maxBetMinor: Number.MAX_SAFE_INTEGER } as const;

// createDiceSDK builds the ready GameSdk for the decided launch. REAL => the shared package's DPoP-bound
// same-origin /v1 adapter (rgsApiMode:"real"); a fresh #t= ticket is handed over as the launchUrl's
// sessionToken, a resume boots with no launchUrl so the SDK restores the persisted session cookie + key.
// FUN => the local deterministic FakePlatformClient injected via deps.platform, on an in-memory SDK store (the
// game only ever holds the opaque GameSdk). A load that is not a launch never gets here (boot.ts blocks it
// first). Always resolves — a failed REAL boot yields an SDK in a needs-relaunch state, surfaced by the caller,
// never a hang.
export async function createDiceSDK(launch: DiceLaunch): Promise<GameSdk> {
  if (launch.kind === "launch-required") throw new Error("no launch: refusing to boot either money mode");
  // The SDK boot loader (SC-61728, Plinko parity): veil by default, `?loader=full|veil|none` per launch, the 2s
  // floor only for the branded splash, and Retry = reload the game (the SDK never reloads by itself). A reload re-runs
  // this boot from the document's URL: #mode=real resumes the persisted session (a real retry for a transient
  // failure); a fresh launch whose exchange never completed is still at #mode=pending, so the reload asks for a
  // casino launch instead of resuming anything.
  const loader = loaderConfig(readLoaderMode(window.location.search), () => window.location.reload());
  const bridge = CASINO_BRIDGE;
  if (launch.realLaunch) {
    // SC-67335: on the game page of Dice's own site the API is the gateway's, cross-origin (api-origin.ts); in the
    // gateway's own shell it is the same origin, as before.
    const api = apiOrigin();
    const sdk = await createGameSDK({
      gameId: "dice",
      rgsApiMode: "real",
      ...(api.kind === "gateway" ? { rgsApiBaseUrl: api.origin } : {}),
      // SC-65843: provably fair on pf tickets (pf-ticket-v1). The player holds each round's commitment before choosing
      // anything; the SDK names the held ticket in the bet and proves every ending on pf.proof. Dual mode: while the
      // server has not put Dice on tickets it answers PF_TICKET_NOT_ENABLED and the SDK plays the legacy bet (pf.ticket
      // says "legacy", and the Provably fair door says so). The FUN demo below runs in the page: no ticket routes.
      provablyFair: "pf-ticket-v1",
      telemetry: telemetryContext(), // env/version so edge-emitted lifecycle events attribute correctly
      ...loader,
      ...bridge,
      ...(launch.launchTicket !== undefined ? { launchUrl: buildLaunchUrl(launch.launchTicket) } : {}),
    });
    // A fresh launch becomes resumable only once its session is READY: from then on the URL says #mode=real. A REAL
    // session that ends fatal changes nothing here — the URL keeps saying REAL, so every reload shows the relaunch
    // message instead of falling into the FUN demo (the old marker self-heal did exactly that). session.ready is a
    // replayed snapshot, so this also fires when the exchange completed inside createGameSDK.
    if (launch.kind === "real-fresh") sdk.on("session.ready", () => markRealSession());
    return sdk;
  }
  // FUN: the SDK boots a session from a launchUrl (parseLaunchHandoff → openSession). The injected fake ignores the
  // sessionToken and returns a demo session, so a fixed placeholder token boots it fully offline; deps.platform (the
  // fake) always wins over the factory, so no network is ever touched; deps.storage keeps the SDK off the REAL record.
  return createGameSDK(
    { gameId: "dice", launchUrl: buildLaunchUrl("demo"), telemetry: telemetryContext(), ...loader, ...bridge },
    { platform: new FakePlatformClient(diceResolver(), FUN_WALLET), storage: memoryStore() },
  );
}

